import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Menu, PhoneCall, LogOut, User as UserIcon, LayoutDashboard } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const links = [
    { to: "/", label: "Home" },
    { to: "/ai-triage", label: "AI Care" },
    { to: "/search", label: "Search" },
    { to: "/book-opd", label: "OPD" },
    { to: "/lab-tests", label: "Lab Tests" },
    { to: "/pharmacy", label: "Pharmacy" },
    { to: "/emergency", label: "Emergency" },
  ];

  const dashboardHref =
    user?.role === "admin" ? "/admin"
    : user?.role === "doctor" ? "/doctor"
    : user?.role === "partner" ? "/partner"
    : user?.role === "driver" ? "/driver"
    : user?.role === "field_executive" ? "/field"
    : "/patient";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/85 border-b border-slate-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <img src="/logo-mark.webp" alt="MedHOne" className="w-9 h-9 object-contain" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-lg tracking-tight">MedH<span className="text-teal-400">One</span></div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-teal-300/80">One Platform, Every Care</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                loc.pathname === l.to
                  ? "bg-teal-500/15 text-teal-300"
                  : "text-slate-300 hover:text-white hover:bg-white/5"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:108"
            data-testid="sos-108-link"
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold emergency-pulse"
          >
            <PhoneCall className="w-4 h-4" /> 108 SOS
          </a>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="ghost" size="sm" className="text-white hover:bg-white/10">
                  <UserIcon className="w-4 h-4 mr-2" />
                  {user.name.split(" ")[0]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-slate-500 capitalize">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav(dashboardHref)}>
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                data-testid="nav-login-btn"
                variant="ghost" size="sm"
                onClick={() => nav("/login")}
                className="text-white hover:bg-white/10"
              >
                Log in
              </Button>
              <Button
                data-testid="nav-register-btn"
                onClick={() => nav("/register")}
                size="sm"
                className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold"
              >
                Get started
              </Button>
            </>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="mobile-menu-btn" variant="ghost" size="sm" className="lg:hidden text-white hover:bg-white/10">
                <Menu className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {links.map((l) => (
                <DropdownMenuItem key={l.to} onClick={() => nav(l.to)}>{l.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
